import React from "react";
import { Control, Controller, FieldValues, Path } from "react-hook-form";
import { Checkbox, FormControlLabel, FormGroup, Theme } from "@mui/material";
import { SxProps } from "@mui/system";

export type Option = {
  label: string;
  value: string;
};

type CheckboxGroupProps<TFieldValues extends FieldValues> = {
  name: Path<TFieldValues>;
  control: Control<TFieldValues>;
  options: Option[];
  sxBox?: SxProps<Theme>;
  sxLabel?: SxProps<Theme>;
};

const STYLES_DEFAULT = {
  box: {
    display: "flex",
    flexDirection: "row",
    columnGap: "30px",
    rowGap: "30px"
  },
  label: {
    mr: 0,
    "& .MuiFormControlLabel-label": {
      fontSize: { xs: "16px", md: "18px", lg: "20px" },
      color: "var(--black)",
      fontWeight: "regular",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
  },
};

/**
 * CheckboxGroupController Component
 *
 * Renders a group of checkboxes integrated with react-hook-form.
 * Allows selection of multiple options and updates form state accordingly.
 * @template TFieldValues - The type of form field values.
 * @param {object} props - Component props.
 * @param {Path<TFieldValues>} props.name - The name of the field in the form.
 * @param {Control<TFieldValues>} props.control - The react-hook-form control object.
 * @param {Option[]} props.options - Array of checkbox options with label and value.
 */
export function CheckboxGroupController<TFieldValues extends FieldValues>({
  name,
  control,
  options,
  sxBox,
  sxLabel,
}: CheckboxGroupProps<TFieldValues>) {
  return (
    <FormGroup sx={{ ...STYLES_DEFAULT.box, ...sxBox } as SxProps<Theme>}>
      {options.map((option, index) => (
        <Controller
          // eslint-disable-next-line react/no-array-index-key
          key={`${option.value}-${index}`}
          name={name}
          control={control}
          render={({ field }) => {
            const isChecked = field.value.includes(option.value);

            const handleChange = (checked: boolean) => {
              const newValue = checked
                ? [...field.value, option.value]
                : field.value.filter((val: string) => val !== option.value);
              field.onChange(newValue);
            };

            return (
              <FormControlLabel
                sx={{ ...STYLES_DEFAULT.label, ...sxLabel }}
                control={
                  <Checkbox
                    color="secondary"
                    checked={isChecked}
                    onChange={(e) => handleChange(e.target.checked)}
                  />
                }
                title={option.label}
                label={option.label}
              />
            );
          }}
        />
      ))}
    </FormGroup>
  );
}
